import type { IotCredentials } from "../api/iot";

/** SigV4 presigned URL for IoT MQTT-over-WebSocket. */
export async function presignIotWebsocket(creds: IotCredentials): Promise<string> {
  const algorithm = "AWS4-HMAC-SHA256";
  const service = "iotdevicegateway";
  const method = "GET";
  const canonicalUri = "/mqtt";

  const now = new Date();
  const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, "");
  const dateStamp = amzDate.slice(0, 8);
  const credentialScope = `${dateStamp}/${creds.region}/${service}/aws4_request`;

  const params = new URLSearchParams({
    "X-Amz-Algorithm": algorithm,
    "X-Amz-Credential": `${creds.access_key_id}/${credentialScope}`,
    "X-Amz-Date": amzDate,
    "X-Amz-Expires": "3600",
    "X-Amz-SignedHeaders": "host",
  });

  const canonicalQueryString = [...params.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${encode(k)}=${encode(v)}`)
    .join("&");

  const canonicalHeaders = `host:${creds.endpoint}\n`;
  const payloadHash = await sha256Hex("");

  const canonicalRequest = [
    method,
    canonicalUri,
    canonicalQueryString,
    canonicalHeaders,
    "host",
    payloadHash,
  ].join("\n");

  const stringToSign = [
    algorithm,
    amzDate,
    credentialScope,
    await sha256Hex(canonicalRequest),
  ].join("\n");

  const kDate = await hmac(`AWS4${creds.secret_access_key}`, dateStamp);
  const kRegion = await hmac(kDate, creds.region);
  const kService = await hmac(kRegion, service);
  const kSigning = await hmac(kService, "aws4_request");
  const signature = bytesToHex(await hmac(kSigning, stringToSign));

  params.set("X-Amz-Signature", signature);
  params.set("X-Amz-Security-Token", creds.session_token);

  return `wss://${creds.endpoint}/mqtt?${params.toString()}`;
}

function encode(s: string) {
  return encodeURIComponent(s).replace(/[!*'()]/g, (c) =>
    `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}

async function sha256Hex(s: string): Promise<string> {
  const buf = new TextEncoder().encode(s);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return bytesToHex(new Uint8Array(digest));
}

async function hmac(key: string | Uint8Array, data: string): Promise<Uint8Array> {
  const raw = typeof key === "string" ? new TextEncoder().encode(key) : key;
  // Ensure we have a plain ArrayBuffer (not SharedArrayBuffer) for importKey.
  const keyBuf: ArrayBuffer = raw.buffer instanceof ArrayBuffer
    ? raw.buffer.slice(raw.byteOffset, raw.byteOffset + raw.byteLength)
    : new Uint8Array(raw).buffer;
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBuf,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", cryptoKey, new TextEncoder().encode(data));
  return new Uint8Array(sig);
}

function bytesToHex(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}
